/**
 * Ventilation Strategy Calculator
 *
 * Determines optimal intake/exhaust vent placement based on heater type
 * (wood vs electric) and ventilation system (natural draft vs mechanical).
 *
 * Supports both Finnish (Harvia downdraft) and Swedish (Tylö self-draft)
 * standards. All positions and text are configurable via Rule Registry.
 */
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';

// ─── Types ──────────────────────────────────────────────────

/** Heater fuel type */
export type HeaterType = 'WOOD' | 'ELECTRIC';

/** Ventilation mechanism */
export type VentSystem = 'NATURAL' | 'MECHANICAL';

export interface VentilationInput {
    /** 'WOOD' (ved) or 'ELECTRIC' (el) */
    heaterType: HeaterType;
    /** 'NATURAL' (självdrag) or 'MECHANICAL' (mekanisk fläkt) */
    ventSystem: VentSystem;
    /** Interior ceiling height in millimetres (standard ≈ 2100) */
    roomHeightMm: number;
    /** Whether the two-room layout has a separate relax room. Defaults to true. */
    hasRelaxRoom?: boolean;
    /** Glazing style affects ventilation leakage assumptions. Defaults to standard. */
    glazingStyle?: 'standard' | 'panorama';
}

export interface VentilationResult {
    /** Intake vent Y-position from floor in mm */
    intakeYMm: number;
    /** Exhaust vent Y-position from floor in mm */
    exhaustYMm: number;
    /** Whether a duct fan is required */
    requiresFan: boolean;
    /** Advisory text explaining the chosen strategy (legacy Swedish from Rule Registry) */
    advice: string;
    /** Translation key for advice — resolves via t[adviceKey] */
    adviceKey:
        | 'domain_vent_woodAdvice'
        | 'domain_vent_elNaturalAdvice'
        | 'domain_vent_elMechAdvice'
        | 'domain_vent_singleRoom_woodAdvice'
        | 'domain_vent_singleRoom_elNaturalAdvice'
        | 'domain_vent_singleRoom_elMechAdvice';
    /** Safety warning (null if none) — legacy Swedish from Rule Registry */
    warning: string | null;
    /** Translation key for warning — resolves via t[warningKey] when set */
    warningKey: VentilationWarningKey | null;
    /**
     * Every warning `warning` concatenates, in the same order. `warningKey`
     * only names the last one, so panorama glazing hid the warning before it.
     */
    warningKeys: VentilationWarningKey[];
    /** Zone model used for ventilation placement. Omitted on legacy two-zone defaults. */
    zoneStrategy?: 'single-room' | 'two-zone';
    /** Zone receiving fresh air. Omitted on legacy two-zone defaults. */
    intakeZone?: 'sauna' | 'relax';
    /** Zone exhausting air. Omitted on legacy two-zone defaults. */
    exhaustZone?: 'sauna' | 'relax';
    /** Rule-backed target air changes per hour. Omitted on legacy two-zone defaults. */
    airChangeRate?: number;
    /** Multiplier used when panorama glazing raises the leakage baseline. */
    infiltrationMultiplier?: number;
}

export type VentilationWarningKey =
    | 'domain_vent_woodMechWarning'
    | 'domain_vent_elNaturalWarning'
    | 'domain_vent_panorama_infiltrationWarning';

type VentilationAdviceKey = VentilationResult['adviceKey'];
type SingleRoomAdviceKey = Extract<VentilationAdviceKey, `domain_vent_singleRoom_${string}`>;

const SINGLE_ROOM_ADVICE_RULES: Record<SingleRoomAdviceKey, string> = {
    domain_vent_singleRoom_woodAdvice: 'ventilation.singleRoom.woodAdvice',
    domain_vent_singleRoom_elNaturalAdvice: 'ventilation.singleRoom.elNaturalAdvice',
    domain_vent_singleRoom_elMechAdvice: 'ventilation.singleRoom.elMechAdvice',
};

// ─── Public API ─────────────────────────────────────────────

/**
 * Calculate optimal ventilation vent positions and strategy based on
 * heater type and ventilation system.
 *
 * All thresholds and advisory text are resolved from the Rule Registry,
 * allowing superuser overrides without code changes.
 */
export function calculateVentilationStrategy(
    input: VentilationInput,
    overrides: RuleOverrides = {},
): VentilationResult {
    const {
        heaterType,
        ventSystem,
        roomHeightMm,
        hasRelaxRoom = true,
        glazingStyle = 'standard',
    } = input;

    if (heaterType === 'WOOD') {
        return applyPanoramaStrategy(
            applyZoneStrategy(
                calculateWoodStrategy(ventSystem, roomHeightMm, overrides),
                hasRelaxRoom,
                glazingStyle,
                overrides,
                'domain_vent_singleRoom_woodAdvice',
            ),
            glazingStyle,
            overrides,
        );
    }

    // ELECTRIC
    if (ventSystem === 'MECHANICAL') {
        return applyPanoramaStrategy(
            applyZoneStrategy(
                calculateElectricMechanicalStrategy(overrides),
                hasRelaxRoom,
                glazingStyle,
                overrides,
                'domain_vent_singleRoom_elMechAdvice',
            ),
            glazingStyle,
            overrides,
        );
    }

    return applyPanoramaStrategy(
        applyZoneStrategy(
            calculateElectricNaturalStrategy(roomHeightMm, overrides),
            hasRelaxRoom,
            glazingStyle,
            overrides,
            'domain_vent_singleRoom_elNaturalAdvice',
        ),
        glazingStyle,
        overrides,
    );
}

// ─── Internal Strategies ────────────────────────────────────

function calculateWoodStrategy(
    ventSystem: VentSystem,
    roomHeightMm: number,
    overrides: RuleOverrides,
): VentilationResult {
    const intakeY = getRuleValue<number>('ventilation.woodIntakeYMm', overrides);
    const exhaustOffset = getRuleValue<number>('ventilation.woodExhaustOffsetMm', overrides);
    const advice = getRuleValue<string>('ventilation.woodAdvice', overrides);
    const mechWarning = getRuleValue<string>('ventilation.woodMechWarning', overrides);

    return {
        intakeYMm: intakeY,
        exhaustYMm: roomHeightMm - exhaustOffset,
        requiresFan: false,
        advice,
        adviceKey: 'domain_vent_woodAdvice',
        warning: ventSystem === 'MECHANICAL' ? mechWarning : null,
        warningKey: ventSystem === 'MECHANICAL' ? 'domain_vent_woodMechWarning' : null,
        warningKeys: ventSystem === 'MECHANICAL' ? ['domain_vent_woodMechWarning'] : [],
    };
}

function calculateElectricNaturalStrategy(
    roomHeightMm: number,
    overrides: RuleOverrides,
): VentilationResult {
    const intakeY = getRuleValue<number>('ventilation.elNaturalIntakeYMm', overrides);
    const exhaustOffset = getRuleValue<number>('ventilation.elNaturalExhaustOffsetMm', overrides);
    const advice = getRuleValue<string>('ventilation.elNaturalAdvice', overrides);
    const warning = getRuleValue<string>('ventilation.elNaturalWarning', overrides);

    return {
        intakeYMm: intakeY,
        exhaustYMm: roomHeightMm - exhaustOffset,
        requiresFan: false,
        advice,
        adviceKey: 'domain_vent_elNaturalAdvice',
        warning,
        warningKey: 'domain_vent_elNaturalWarning',
        warningKeys: ['domain_vent_elNaturalWarning'],
    };
}

function calculateElectricMechanicalStrategy(
    overrides: RuleOverrides,
): VentilationResult {
    const intakeY = getRuleValue<number>('ventilation.elMechIntakeYMm', overrides);
    const exhaustY = getRuleValue<number>('ventilation.elMechExhaustYMm', overrides);
    const advice = getRuleValue<string>('ventilation.elMechAdvice', overrides);

    return {
        intakeYMm: intakeY,
        exhaustYMm: exhaustY,
        requiresFan: true,
        advice,
        adviceKey: 'domain_vent_elMechAdvice',
        warning: null,
        warningKey: null,
        warningKeys: [],
    };
}

function applyZoneStrategy(
    result: VentilationResult,
    hasRelaxRoom: boolean,
    glazingStyle: VentilationInput['glazingStyle'],
    overrides: RuleOverrides,
    singleRoomAdviceKey: SingleRoomAdviceKey,
): VentilationResult {
    if (hasRelaxRoom) {
        if (glazingStyle !== 'panorama') {
            return result;
        }

        return {
            ...result,
            zoneStrategy: 'two-zone',
            intakeZone: 'sauna',
            exhaustZone: 'relax',
        };
    }

    const airChangeRate = getRuleValue<number>('ventilation.singleRoom.airChangeRate', overrides);
    const advice = getRuleValue<string>(SINGLE_ROOM_ADVICE_RULES[singleRoomAdviceKey], overrides);

    return {
        ...result,
        advice,
        adviceKey: singleRoomAdviceKey,
        zoneStrategy: 'single-room',
        intakeZone: 'sauna',
        exhaustZone: 'sauna',
        airChangeRate,
    };
}

function applyPanoramaStrategy(
    result: VentilationResult,
    glazingStyle: VentilationInput['glazingStyle'],
    overrides: RuleOverrides,
): VentilationResult {
    if (glazingStyle !== 'panorama') {
        return result;
    }

    const infiltrationMultiplier = getRuleValue<number>(
        'ventilation.panorama.infiltrationMultiplier',
        overrides,
    );
    const panoramaWarning = getRuleValue<string>(
        'ventilation.panorama.infiltrationWarning',
        overrides,
    );

    return {
        ...result,
        infiltrationMultiplier,
        warning: result.warning ? `${result.warning} ${panoramaWarning}` : panoramaWarning,
        warningKey: 'domain_vent_panorama_infiltrationWarning',
        warningKeys: [...result.warningKeys, 'domain_vent_panorama_infiltrationWarning'],
    };
}
